<template>
    <SbsPopper
        theme="sbs-popup"
        :autoHide="closeOnClickOutside"
        :strategy="strategy"
        :triggers="[]"
        :container="parentSelector"
        :distance="offset"
        :placement="position"
        :shown="isPopupOpen"
        :fullWidth="fullWidth"
        @keyup.esc="keyupEscHandler"
        @autoHide="close">
        <template #default="{ toggle }">
            <!-- <div
                class="sbs-popup__activator"
                @click="togglePopup(!isPopupOpen)"> -->
                <slot @click="toggle"></slot>
            <!-- </div> -->
        </template>
        <template #content>
            <div
                class="sbs-popup"
                :class="popupClass">
                <div
                    class="sbs-popup__content custom-scrollbar"
                    :class="popupContentClass"
                    :style="dimensionStyles"
                >
                    <slot
                        v-if="showCloseButton"
                        name="closeButton">
                        <SbsButtonIcon
                            class="sbs-popup__close-button"
                            width="15"
                            height="15"
                            iconName="close"
                            colorTheme="gray"
                            hoverColor="black"
                            @click="close" />
                    </slot>
                    <slot name="content">{{ content }}</slot>
                </div>
            </div>
        </template>
    </SbsPopper>
</template>

<script>
import dimensionsMixin from '../../mixins/dimensions.js';
import {
    AllPositions,
    FLOAT_POSITIONS,
} from '../../utils/positions.js';
import { AllTransitions } from '../../utils/transitions.js';
import {
    isArrayContainsValue,
    isValueConvertedToNumber,
} from '../../utils/validators.js';
import SbsButtonIcon from '../button/SbsButtonIcon.vue';
import SbsPopper from '../popper/SbsPopper.vue';

/**
 * Компонент попапа
 */
export default {
    name: 'SbsPopup',
    components: {
        SbsPopper,
        SbsButtonIcon,
    },
    mixins: [dimensionsMixin],
    emits: ['update:modelValue'],
    computed: {
        isPopupOpen: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },
        strategy() {
            return this.fixed ? 'fixed' : 'absolute';
        },
    },
    methods: {
        open() {
            this.isPopupOpen = true;
        },
        close() {
            this.isPopupOpen = false;
        },
        toggle() {
            this.isPopupOpen = !this.isPopupOpen;
        },
        keyupEscHandler() {
            if (this.closeOnEscPress) this.close();
        },
    },
    props: {
        /**
         * Должен ли попап закрываться по клику вне его области
         */
        closeOnClickOutside: {
            type: Boolean,
            default: true,
        },
        /**
         * Должен ли попап закрываться по нажатию на клавишу Escape
         */
        closeOnEscPress: {
            type: Boolean,
            default: true,
        },
        /**
         * Контент попапа
         */
        content: {
            type: String,
            default: '',
        },
        /**
         * Устанавливает `position: fixed` для попапа
         */
        fixed: {
            type: Boolean,
            default: false,
        },
        /**
         * HTML элемент, в котором изначально рендерится попап
         *
         * Если не передан, то будет использован `element.parentElement`
         *
         * Используется для возврата элемента попапа в изначальный контейнер
         * после деактивации
         */
        initialContainer: {
            type: [HTMLElement, null],
            default: null,
        },
        /**
         * Отступ между попапом и активатором
         */
        offset: {
            type: [Number, String],
            default: 10,
            validator: isValueConvertedToNumber,
        },
        /**
         * Селектор контейнера, куда будет рендериться попап
         *
         * Если не указан, то будет использован `document.body`
         */
        parentSelector: {
            type: String,
            default: 'body',
        },
        /**
         * Класс для попапа
         */
        popupClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для контента попапа
         */
        popupContentClass: {
            type: String,
            default: '',
        },
        /**
         * Выравнивание попапа относительно активатора
         *
         * Валидны следующие значения позиции: `top`, `right`, `bottom`, `left`
         *
         * Также можно указать модификаторы для выравнивания
         * относительно края: `-start`, `-end`
         *
         * Примеры использования: `top`, `left-start`, `bottom-end`
         */
        position: {
            type: String,
            default: AllPositions.BOTTOM,
            validator: isArrayContainsValue(FLOAT_POSITIONS),
        },
        /**
         * Определяет видимость кнопки закрытия попапа
         */
        showCloseButton: {
            type: Boolean,
            default: true,
        },
        /**
         * Название класса для анимации открытия/закрытия попапа
         */
        transitionName: {
            type: String,
            default: AllTransitions.FADE_DOWN,
        },
        /**
         * Определяет виден ли попап
         * @model
         */
        modelValue: {
            type: Boolean,
            default: true,
        },
        /**
         * `z-index`, используемый для попапа
         */
        zIndex: {
            type: [Number, String],
            default: 100,
            validator: isValueConvertedToNumber,
        },
        /**
         * `full-wdth`, определяет, расстягивается ли контейнер попапа на всю ширину
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
    },
};
</script>

<style lang="scss">
.v-popper--theme-sbs-popup {
    display: flex;

    .v-popper__inner {
        padding: 0;
        overflow: visible;
        background-color: transparent;
    }

    .v-popper__arrow-container {
        display: none;
    }
}

.sbs-popup {
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    padding: 30px;
    background-color: $white;
    border-radius: $rounded-15;
    box-shadow: $shadow-default;

    @include typo-secondary-book;

    &__close-button {
        position: absolute;
        top: 15px;
        right: 15px;
    }

    &__content {
        display: flex;
        flex-direction: column;
        overflow: auto;
        word-wrap: break-word;
    }
}
</style>
